import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AVERTISSEMENT_DOC,
  LIBELLES_MODE_RATTACHEMENT_CV,
  LIBELLES_MOTIF_ATTENTE_CV,
  LIBELLES_STATUT_DEPOT_CV,
  telephoneLisible,
  type BilanSuppressionCandidature,
  type CandidatProbable,
  type ChampCoordonneeCv,
  type CreationCandidatureCv,
  type DepotCvDetail,
  type ReponseActionDepotCv,
  type ReponseCandidatureCv,
} from '@rh/shared';
import { appel } from '../api';
import { formaterTaille, libelleEffacement, messageCompletion } from '../envoi-cv';
import { dateHeure, nomComplet } from '../format';
import { telechargerDocument } from '../telechargement';
import { ChoixCandidatureCv, RechercheFicheCv, type CandidatureProposee } from './ChoixCandidatureCv';
import { CoordonneesLues, PropositionsCoordonnees } from './CoordonneesLues';
import { CreerCandidatureCv } from './CreerCandidatureCv';
import { DeplacerCv } from './DeplacerCv';
import { Erreur } from './Etat';
import { LecteurDocument } from './LecteurDocument';

export interface NoticeCv {
  texte: string;
  alerte: boolean;
  candidatureId?: string;
}

interface Props {
  depot: DepotCvDetail;
  /** Action faite, ou dépôt traité entre-temps ailleurs : la page affiche le message et relit sa liste. */
  onFait: (notice: NoticeCv) => void;
}

/** Refus qui disent que la carte est périmée : un autre poste (ou le worker) a déjà traité ce CV. */
const CODES_PERIMES = new Set(['DEPOT_CV_DEJA_TRAITE', 'DEPOT_CV_NON_RATTACHE', 'DEPOT_CV_INTROUVABLE']);

const jour = (iso: string | null) => (iso ? dateHeure(iso) : '—');

/** Un CV déposé sur la page « CV à rattacher », selon son état. */
export function CarteDepotCv({ depot, onFait }: Props) {
  const [ouvert, setOuvert] = useState(false);
  const [panneau, setPanneau] = useState<'recherche' | 'deplacer' | 'annonce' | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ajoutTelephone, setAjoutTelephone] = useState('');
  const [ajoutEmail, setAjoutEmail] = useState('');

  const document = depot.document;
  const candidature = depot.candidature;
  const enAttente = depot.statut === 'analyse' || depot.statut === 'a_rattacher';

  async function executer(travail: () => Promise<NoticeCv>): Promise<void> {
    setEnvoi(true);
    setErreur(null);
    try {
      onFait(await travail());
    } catch (e) {
      const code = (e as { code?: unknown } | null)?.code;
      const message = e instanceof Error ? e.message : 'Opération impossible';
      if (typeof code === 'string' && CODES_PERIMES.has(code)) onFait({ texte: message, alerte: true });
      else setErreur(message);
    } finally {
      setEnvoi(false);
    }
  }

  function rattacher(candidat: CandidatProbable, cible: CandidatureProposee): void {
    // Complétion demandée seulement pour une coordonnée que la fiche choisie n'a pas.
    const telephone = ajoutTelephone && !candidat.telephone ? ajoutTelephone : undefined;
    const email = ajoutEmail && !candidat.email ? ajoutEmail : undefined;
    const destination = `${nomComplet(candidat)} — ${cible.annonceIntitule}`;
    const ajouts = [
      telephone ? `le téléphone ${telephoneLisible(telephone)}` : null,
      email ? `l’e-mail ${email}` : null,
    ].filter(Boolean);
    const question = `Rattacher ce CV à ${destination} ?${ajouts.length > 0 ? ` La fiche recevra ${ajouts.join(' et ')}.` : ''}`;
    if (!window.confirm(question)) return;
    void executer(async () => {
      const reponse = await appel<ReponseActionDepotCv>(`/cv-a-rattacher/${depot.id}/rattacher`, {
        method: 'POST',
        body: JSON.stringify({ candidatureId: cible.candidatureId, telephone, email }),
      });
      const completion = messageCompletion(reponse.completion);
      return {
        texte: `CV rattaché à ${destination}.${completion ? ` ${completion.texte}` : ''}`,
        alerte: completion?.alerte ?? false,
        candidatureId: cible.candidatureId,
      };
    });
  }

  /** « Affecter à une annonce » : fiche et candidature créées depuis le CV. */
  function creerCandidature(corps: CreationCandidatureCv, destination: string): void {
    void executer(async () => {
      const reponse = await appel<ReponseCandidatureCv>(`/cv-a-rattacher/${depot.id}/creer-candidature`, {
        method: 'POST',
        body: JSON.stringify(corps),
      });
      const fiche = reponse.personneCreee
        ? ''
        : ' Une fiche portait déjà cette coordonnée : elle a servi, sans doublon.';
      return {
        texte: reponse.candidatureCreee
          ? `Candidature créée : ${destination}. Le CV y est rattaché.${fiche}`
          : `Ce candidat était déjà sur cette annonce : le CV a rejoint sa candidature.${fiche}`,
        alerte: !reponse.candidatureCreee,
        candidatureId: reponse.candidatureId,
      };
    });
  }

  function ecarter(): void {
    if (!window.confirm('Écarter ce CV ? Le fichier sera effacé.')) return;
    void executer(async () => {
      await appel<ReponseActionDepotCv>(`/cv-a-rattacher/${depot.id}/ecarter`, { method: 'POST' });
      return { texte: 'CV écarté : le fichier est effacé.', alerte: false };
    });
  }

  function completer(champ: ChampCoordonneeCv, valeur: string): void {
    void executer(async () => {
      const reponse = await appel<ReponseActionDepotCv>(`/cv-a-rattacher/${depot.id}/completer`, {
        method: 'POST',
        body: JSON.stringify({ [champ]: valeur }),
      });
      const completion = messageCompletion(reponse.completion);
      return {
        texte: completion?.texte ?? 'Fiche complétée.',
        alerte: completion?.alerte ?? false,
        candidatureId: candidature?.candidatureId,
      };
    });
  }

  /**
   * « Supprimer la candidature » : défait une affectation qu'on vient de faire.
   * Irréversible — d'où le rappel de ce qui part, et la fiche citée par son nom.
   */
  function supprimerCandidature(): void {
    if (!candidature) return;
    const qui = `${nomComplet(candidature)} — ${candidature.annonceIntitule}`;
    const question =
      `Supprimer définitivement la candidature de ${qui} ?\n\n` +
      'Le CV, les appels, l’évaluation, la charte et les justificatifs de ce dossier seront effacés. ' +
      'Si cette personne n’a pas d’autre candidature, sa fiche sera supprimée avec.';
    if (!window.confirm(question)) return;
    void executer(async () => {
      const bilan = await appel<BilanSuppressionCandidature>(`/cv-a-rattacher/${depot.id}/supprimer-candidature`, {
        method: 'POST',
      });
      return {
        texte:
          `Candidature supprimée : ${qui}.` +
          (bilan.personneSupprimee ? ' Sa fiche, sans autre candidature, a été supprimée aussi.' : ''),
        alerte: false,
      };
    });
  }

  function retirer(): void {
    if (!window.confirm('Retirer ce CV de la fiche ? Le fichier sera effacé ; les coordonnées déjà ajoutées restent.')) {
      return;
    }
    void executer(async () => {
      await appel<ReponseActionDepotCv>(`/cv-a-rattacher/${depot.id}/retirer`, { method: 'POST' });
      return { texte: 'CV retiré de la fiche : le fichier est effacé, les coordonnées déjà ajoutées restent.', alerte: false };
    });
  }

  const completable = depot.coordonnees.telephones.length > 0 || depot.coordonnees.emails.length > 0;

  return (
    <li className={`carte-depot-cv carte-depot-cv--${depot.statut}`}>
      <div className="entete-depot">
        <strong className="nom-depot">{depot.nomOrigine ?? 'CV sans nom'}</strong>
        <span className={`badge-depot badge-depot--${depot.statut}`}>{LIBELLES_STATUT_DEPOT_CV[depot.statut]}</span>
      </div>
      <p className="meta">
        <span>{depot.format.toUpperCase()}</span>
        <span>{formaterTaille(depot.taille)}</span>
        <span>
          déposé le {dateHeure(depot.deposeLe)} par {depot.deposePar}
        </span>
      </p>
      {depot.expireLe && enAttente && (
        <p className="mention">Effacé {libelleEffacement(depot.expireLe)} s’il n’est pas rattaché.</p>
      )}

      {document && (
        <>
          {depot.format === 'doc' && <p className="mention">{AVERTISSEMENT_DOC}.</p>}
          <div className="actions-depot">
            {depot.format !== 'doc' && (
              <button
                type="button"
                className="bouton-second"
                aria-expanded={ouvert}
                onClick={() => setOuvert((actuel) => !actuel)}
              >
                {ouvert ? 'Fermer le CV' : 'Voir le CV'}
              </button>
            )}
            <button
              type="button"
              className="lien"
              onClick={() => void telechargerDocument(document.id, document.nomFichier)}
            >
              Télécharger
            </button>
          </div>
          {ouvert && <LecteurDocument document={document} />}
        </>
      )}

      {depot.statut === 'analyse' && (
        <p className="lecture-cv" aria-live="polite">
          Lecture du CV en cours…
        </p>
      )}

      {depot.statut === 'a_rattacher' && (
        <>
          {depot.motifAttente && <p className="motif-cv">{LIBELLES_MOTIF_ATTENTE_CV[depot.motifAttente]}</p>}
          <CoordonneesLues coordonnees={depot.coordonnees} />
          <h4>Candidats probables</h4>
          {depot.candidatsProbables.length === 0 ? (
            <p className="mention">Aucun candidat probable : cherchez la fiche.</p>
          ) : (
            <ChoixCandidatureCv
              candidats={depot.candidatsProbables}
              libelleAction="Rattacher ici"
              envoi={envoi}
              onChoisir={rattacher}
            />
          )}
          {completable && (
            <fieldset className="completion-cv">
              <legend>Compléter la fiche choisie</legend>
              <p className="mention">Seulement si elle n’a pas encore cette coordonnée : jamais par-dessus.</p>
              {depot.coordonnees.telephones.length > 0 && (
                <div className="choix-completion">
                  <label className="case">
                    <input
                      type="radio"
                      name={`telephone-${depot.id}`}
                      checked={ajoutTelephone === ''}
                      onChange={() => setAjoutTelephone('')}
                    />
                    <span>Ne pas ajouter de téléphone</span>
                  </label>
                  {depot.coordonnees.telephones.map((telephone) => (
                    <label key={telephone} className="case">
                      <input
                        type="radio"
                        name={`telephone-${depot.id}`}
                        checked={ajoutTelephone === telephone}
                        onChange={() => setAjoutTelephone(telephone)}
                      />
                      <span>Ajouter le téléphone {telephoneLisible(telephone)}</span>
                    </label>
                  ))}
                </div>
              )}
              {depot.coordonnees.emails.length > 0 && (
                <div className="choix-completion">
                  <label className="case">
                    <input
                      type="radio"
                      name={`email-${depot.id}`}
                      checked={ajoutEmail === ''}
                      onChange={() => setAjoutEmail('')}
                    />
                    <span>Ne pas ajouter d’e-mail</span>
                  </label>
                  {depot.coordonnees.emails.map((email) => (
                    <label key={email} className="case">
                      <input
                        type="radio"
                        name={`email-${depot.id}`}
                        checked={ajoutEmail === email}
                        onChange={() => setAjoutEmail(email)}
                      />
                      <span>Ajouter l’e-mail {email}</span>
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
          )}
        </>
      )}

      {enAttente && panneau === 'recherche' && (
        <div className="panneau-cv">
          <h4>Choisir une autre fiche</h4>
          <RechercheFicheCv libelleAction="Rattacher ici" envoi={envoi} onChoisir={rattacher} />
          <div className="actions">
            <button type="button" className="lien" onClick={() => setPanneau(null)}>
              Fermer la recherche
            </button>
          </div>
        </div>
      )}
      {enAttente && panneau === 'annonce' && (
        <CreerCandidatureCv
          depot={depot}
          envoi={envoi}
          onAnnuler={() => setPanneau(null)}
          onValider={creerCandidature}
        />
      )}
      {enAttente && document && (
        <div className="actions-depot">
          {panneau !== 'recherche' && (
            <button type="button" className="bouton-second" onClick={() => setPanneau('recherche')}>
              {depot.statut === 'analyse' ? 'Choisir la fiche' : 'Choisir un autre candidat'}
            </button>
          )}
          {panneau !== 'annonce' && (
            <button type="button" className="bouton-second" disabled={envoi} onClick={() => setPanneau('annonce')}>
              Affecter à une annonce
            </button>
          )}
          <button type="button" className="bouton-second bouton-danger" disabled={envoi} onClick={ecarter}>
            Écarter
          </button>
        </div>
      )}

      {depot.statut === 'rattache' && candidature && (
        <>
          <p className="rattache-a">
            Rattaché à <Link to={`/candidatures/${candidature.candidatureId}`}>{nomComplet(candidature)}</Link> —{' '}
            {candidature.annonceIntitule}
            {candidature.annonceReference && (
              <>
                {' '}
                <code>{candidature.annonceReference}</code>
              </>
            )}
          </p>
          <p className="mention">
            {depot.mode ? LIBELLES_MODE_RATTACHEMENT_CV[depot.mode] : 'Rattaché'} le {jour(depot.traiteLe)}
            {depot.traitePar ? ` par ${depot.traitePar}` : ''}
          </p>
          {!document && <p className="mention">Fichier effacé avec la fiche.</p>}
          {document && !candidature.anonymisee && (
            <>
              <PropositionsCoordonnees
                coordonnees={depot.coordonnees}
                telephoneFiche={candidature.telephone}
                emailFiche={candidature.email}
                envoi={envoi}
                onAjouter={completer}
              />
              {panneau === 'deplacer' ? (
                <DeplacerCv
                  depotId={depot.id}
                  candidatureActuelle={candidature.candidatureId}
                  nomActuel={nomComplet(candidature)}
                  onAnnuler={() => setPanneau(null)}
                  onDeplace={(reponse, destination) =>
                    onFait({
                      texte: `CV déplacé vers ${destination}.`,
                      alerte: false,
                      candidatureId: reponse.depot.candidature?.candidatureId,
                    })
                  }
                />
              ) : (
                <div className="actions-depot">
                  <button
                    type="button"
                    className="bouton-second"
                    disabled={envoi}
                    onClick={() => setPanneau('deplacer')}
                  >
                    Déplacer vers une autre candidature
                  </button>
                  <button type="button" className="bouton-second bouton-danger" disabled={envoi} onClick={retirer}>
                    Retirer (fichier effacé)
                  </button>
                  <button
                    type="button"
                    className="bouton-second bouton-danger"
                    disabled={envoi}
                    onClick={supprimerCandidature}
                  >
                    Supprimer la candidature
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {depot.statut === 'ecarte' && (
        <p className="mention">
          Écarté le {jour(depot.traiteLe)}
          {depot.traitePar ? ` par ${depot.traitePar}` : ''}
        </p>
      )}
      {depot.statut === 'retire' && (
        <p className="mention">
          Retiré d’une fiche le {jour(depot.traiteLe)}
          {depot.traitePar ? ` par ${depot.traitePar}` : ''}
        </p>
      )}
      {depot.statut === 'expire' && (
        <p className="mention">Effacé automatiquement le {jour(depot.traiteLe)}, faute de rattachement</p>
      )}
      {erreur && <Erreur message={erreur} />}
    </li>
  );
}
